"use client";

import { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/Card";
import type { MemberMonthlyParams } from "api//businessDashboard";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import type {
  MemberMetric,
  MonthlyMemberDetail,
} from "types/BusinessDashboard";
import { useMemberDetailQuery } from "queries/Business/Dashboard/useDashboardQueries";
import {
  MEMBERS_LIGHT_BREAKDOWN_COLORS,
  MEMBERS_PASS_TYPE_MAP,
  membersDashboardTabOptions,
} from "constants/businessDashboard";
import {
  ChartError,
  ChartSkeleton,
  CustomTooltip,
  LastDot,
} from "./chart-ui-kit";

const StyledCard = styled(Card)`
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding-top: 24px;
  padding-bottom: 0;
  margin-bottom: 12px;
  gap: 0;
`;

const StyledCardHeader = styled(CardHeader)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 16px;
  margin-bottom: 12px;
`;

const StyledCardTitle = styled(CardTitle)`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #363e52;
  padding: 0;
`;

const StyledCardContent = styled(CardContent)`
  padding: 0 16px;
`;

const ChartContainer = styled.div`
  height: 300px;
  width: 100%;
`;

export const MembersChartSection = () => {
  const { searchParams } = useUpdateUrlParams();
  const activeTab = searchParams.get("tab") || membersDashboardTabOptions[0].id;
  const endMonthParam =
    searchParams.get("endMonth") || dayjs().format("YYYY-MM");
  const endDate = dayjs(endMonthParam);
  const referenceEndMonth = useMemo(() => endDate.format("YYYY-MM"), [endDate]);
  const referenceStartMonth = useMemo(
    () => endDate.subtract(5, "month").format("YYYY-MM"),
    [endDate]
  );

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const detailApiParams = useMemo(() => {
    const params: MemberMonthlyParams = {
      referenceStartMonth,
      referenceEndMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }
    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [partnerInfo, referenceEndMonth, referenceStartMonth]);

  const {
    data: detailData,
    isLoading,
    isError,
  } = useMemberDetailQuery(detailApiParams);

  const chartTitle =
    activeTab === "total"
      ? "총 유효 회원수"
      : `${MEMBERS_PASS_TYPE_MAP[activeTab]?.title} 유효 회원수`;

  const mainLineColor = MEMBERS_LIGHT_BREAKDOWN_COLORS[activeTab] || "#0044CC";

  if (isLoading) return <ChartSkeleton title={chartTitle} />;
  if (isError || !detailData) return <ChartError />;

  const { charts } = (() => {
    if (!detailData)
      return { charts: { totalMembersData: [], selectedTabData: [] } };

    const totalMembersData = detailData.data.map((m) => ({
      month: m.referenceMonth,
      value: m.total.count,
      changeRate: m.total.changeRateComparedToPreviousMonth,
    }));

    const selectedTabData = detailData.data.map((m) => {
      const key = MEMBERS_PASS_TYPE_MAP[activeTab].apiKey as keyof Omit<
        MonthlyMemberDetail,
        "referenceMonth"
      >;
      const metric = m[key] as MemberMetric;

      return {
        month: m.referenceMonth,
        value: metric?.count ?? 0,
        changeRate: metric?.changeRateComparedToPreviousMonth,
      };
    });

    return { charts: { totalMembersData, selectedTabData } };
  })();

  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledCardTitle>{chartTitle}</StyledCardTitle>
      </StyledCardHeader>
      <StyledCardContent>
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart margin={{ top: 8, right: -28, left: 0, bottom: 13 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#D9DDE5"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={{ stroke: "#D9DDE5" }}
                tick={{
                  fontSize: 12,
                  fontWeight: 600,
                  fill: "#70747D",
                  letterSpacing: -0.2,
                }}
                tickFormatter={(value) => `${dayjs(value).format("M")}월`}
                allowDuplicatedCategory={false}
                interval={0}
                height={56}
                tickMargin={13}
                padding={{
                  left: 15.5,
                  right: 15.5,
                }}
              />
              <YAxis
                orientation="right"
                tickLine={false}
                axisLine={{ stroke: "#D9DDE5" }}
                tick={{ fontSize: 12, fontWeight: 500, fill: "#70747D" }}
                interval={0}
                tickMargin={4}
                domain={[
                  0,
                  Math.max(...charts.totalMembersData.map((d) => d.value)) *
                    1.2,
                ]}
                tickFormatter={(value) =>
                  `${Math.floor(value).toLocaleString()}`
                }
              />
              <Tooltip
                trigger="click"
                content={
                  <CustomTooltip
                    activeTab={activeTab}
                    selectedYear={endDate.year()}
                  />
                }
              />
              {activeTab !== "total" && (
                <Line
                  type="linear"
                  data={charts.totalMembersData}
                  dataKey="value"
                  stroke="#0044CC33"
                  strokeWidth={3}
                  dot={false}
                  activeDot={false}
                  name="totalMembers"
                  isAnimationActive={false}
                />
              )}
              <Line
                type="linear"
                data={charts.selectedTabData}
                dataKey="value"
                stroke={mainLineColor}
                strokeWidth={3}
                dot={
                  <LastDot
                    color={mainLineColor}
                    chartData={charts.selectedTabData}
                  />
                }
                activeDot={{ r: 5, fill: mainLineColor }}
                name="selectedMembers"
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </StyledCardContent>
    </StyledCard>
  );
};
